import { createContext } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Entitlements, EntitlementFlag } from './types'

export interface EntitlementsCtxValue {
  data?: Entitlements
  isLoading: boolean
  isEnabled(flag: EntitlementFlag | string): boolean
  limit(key: string): number | null
  value(key: string): number
  regionAllowed(region: string): boolean
}

export const EntitlementsContext = createContext<EntitlementsCtxValue | null>(null)

export function EntitlementsProvider({
  children,
  loadingFallback = null,
}: {
  children: ReactNode
  loadingFallback?: ReactNode
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.get<Entitlements>('/api/entitlements'),
  })

  if (isError) {
    console.warn('[EntitlementsProvider] Manifest konnte nicht geladen werden – App läuft ohne Lizenz-Gate.')
  }

  const value: EntitlementsCtxValue = {
    data,
    isLoading,
    isEnabled: (flag) => data?.flags?.[flag] === true,
    limit: (key) => data?.limits?.[key] ?? null,
    value: (key) => data?.values?.[key] ?? 0,
    regionAllowed: (region) =>
      !data || data.allowedRegions.length === 0 || data.allowedRegions.includes(region),
  }

  // Kinder erst rendern, wenn der erste Manifest-Load abgeschlossen ist
  // (Erfolg ODER Fehler). Verhindert Gating-Flash und fälschliche Deep-Link-
  // Umleitung im Lade-Fenster. Analog zum RequireEnabled-Muster.
  if (isLoading) return <>{loadingFallback}</>

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>
}
